// Bare shared-library import, not a deep subpath — a per-file deep specifier
// into @symbo-ls/workspace-shared (a restricted/private-registry package)
// cannot resolve through the platform's public importmap once this package
// is pushed as a library (tickets/execution-board.md §1 P0). Deferred-call
// wrapper, not a module-scope destructure — see app.js's circular-init note.
import _wsShared from '@symbo-ls/workspace-shared'
const shellGetSDK = (...args) => _wsShared.functions.getSDK(...args)
const shellGetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.getActiveWorkspacePointer(...args)
const shellSetActiveWorkspacePointer = (...args) =>
  _wsShared.functions.setActiveWorkspacePointer(...args)
const shellIsMwtEnabled = (...args) => _wsShared.functions.isMwtEnabled(...args)
const shellConsumeOAuthNext = (...args) => _wsShared.functions.consumeOAuthNext(...args)
const shellSetActiveWorkspaceRecord = (...args) => _wsShared.functions.setActiveWorkspaceRecord(...args)
const shellStampWorkspaceShape = (...args) => _wsShared.functions.stampWorkspaceShape(...args)
const shellNotifyWorkspaceIdentityCorrected = (...args) =>
  _wsShared.functions.notifyWorkspaceIdentityCorrected(...args)
// Deferred wrapper, never a module-scope destructure and NEVER a deep subpath
// import — `@symbo-ls/workspace-shared` is a restricted-registry package whose
// per-file specifiers cannot resolve through the platform's public importmap;
// a deep import of this exact shape once killed the whole shell catalog page
// (see the header of app.js). Bare package + `.functions.X`, like every other
// borrowed helper in this file.
//
// `shell`-prefixed like the rest of this block, never shared's own name: frank
// hoists a module helper into the library's ONE flat globalScope under its
// name, and shared hoists its real `scopeWorkspaceListToWhitelabelHost` there
// too — two definitions of one name in the catalog, resolved by merge order
// (globalScopeNameCensus).
const shellScopeClaimsToWhitelabelHost = (...args) =>
  _wsShared.functions.scopeClaimsToWhitelabelHost(...args)
const shellScopeWorkspaceListToWhitelabelHost = (...args) =>
  _wsShared.functions.scopeWorkspaceListToWhitelabelHost(...args)
const shellStampWorkspaceListShape = (...args) => _wsShared.functions.stampWorkspaceListShape(...args)
const shellWorkspaceDisplayName = (...args) => _wsShared.functions.workspaceDisplayName(...args)
const shellGetWorkspacePrefixSlug = (...args) => _wsShared.functions.getWorkspacePrefixSlug(...args)
// Cross-origin auth handoff (authHandoff.js in workspace-shared). Same
// deferred-call wrapper shape as the rest of this block. Used by
// `redirectIfUnauthed`'s `authed && onAuthPage` branch — see the comment
// there (PREVIEW-AUTH-HANDOFF-NO-RETURN-LOCAL-1).
const shellIsTrustedHandoffTarget = (...args) => _wsShared.functions.isTrustedHandoffTarget(...args)
const shellClassifyHandoffTarget = (...args) => _wsShared.functions.classifyHandoffTarget(...args)
const shellEncodeHandoffPayload = (...args) => _wsShared.functions.encodeHandoffPayload(...args)
const shellAppendHandoffFragment = (...args) => _wsShared.functions.appendHandoffFragment(...args)
import {
  loadWorkspaceProject,
  loadWorkspaceApps,
  getModuleLoadOutcome,
  getAppsLoadOutcome,
  whenAppRouteReady
} from './workspaceProjects.js'
import { applyWorkspaceConfig } from './workspaceConfigs.js'
import { isDemoHost } from './demoHost.js'
import { getHostScopeOrgId } from './hostScopePin.js'
import { isCustomDomainHost } from './customDomainHost.js'
import { withLiveQuery } from './bootRedirectQuery.js'
import {
  _dashboardPrefsFetches,
  awaitInitialWorkspace,
  hasAuthoritativeWorkspace,
  beginPostAuthWorkspaceReveal as _bpwrDirect,
  endPostAuthWorkspaceReveal as _epwrDirect
} from './loadPrivateData.js'
import { getWorkspaceCached as _gwcDirect } from './getWorkspaceCached.js'
import {
  hasSessionToken,
  AUTH_PATHS,
  authRedirectTarget,
  isChromelessShellPath
} from './authGate.js'
// The session gate's pure pieces live in ./authGate.js (the comm app registers
// them without this module); re-exported so every caller keeps this path.
export { hasSessionToken, AUTH_PATHS, authRedirectTarget, isChromelessShellPath }
// (getWorkspacePrefixSlug rides the single bare `_wsShared` import + wrapper
// block at the top of this file.)
//
// beginPostAuthWorkspaceReveal / endPostAuthWorkspaceReveal / getWorkspaceCached
// are reached via `root.call(...)` at their call sites below, preferred over
// the direct imports above — both close over module-private state (a WeakMap
// / an in-flight dedup cache) in their own file, so a raw cross-file `import`
// reference is a free identifier once `subscribeAuthSession` is serialized
// and revived standalone. `root.call` reaches the live singleton through
// `context.functions` instead (see loadPrivateData.js's identical note).
// HARDENED (ROOT-CALL-REF-1, 2026-08-15): `root.call` is ABSENT on some
// state objects mid-switch — bellf-qa captured the TypeError thrown
// synchronously from a sibling wrapper inside onCreate (rootBus replays
// orgSwitched to a late subscriber during createDomqlElement), and an
// uncaught lifecycle throw aborts element creation mid-tree, leaving later
// handlers unbound. The three wrappers below fall back to the direct
// imports above when `root.call` isn't callable — same in-bundle singleton
// under normal execution; under frank revival the import is an unresolved
// free identifier and `typeof` on it is safely 'undefined', degrading to a
// no-op instead of a throw.
// Canonical signout lives in shared and shared registers it
// (`el.call('signoutAndRedirect')`); the shell does not re-register it — a
// second definition under the same key collides in a catalog merge.

// Resolve the active-org id across the layered sources that can name one.
// Order:
//   1. `root._pendingActiveOrgId` — the optimistic pick set THIS tick by
//      AppOrgSwitcherItem.onClick. Read by switcher consumers immediately
//      while the `PATCH /auth/me/active-org` write is in flight.
//   2. `user.activeOrganization` (Mongo) — the persisted user choice,
//      replicated across every device the user signs in on. Set by
//      `sdk.setActiveOrganization()`; received in the SDK session's
//      user payload and via the socket.io `active-org-changed` event.
//   3. `app_metadata` claims — `activeWorkspace.org_id` / `workspace_org_id`.
//      Authoritative for the workspace scope.
//   4. First org in the claim list — fresh sign-in default.
//
// Validates the resolved id is in the user's org list — a stale stored
// id (membership revoked) can't ghost-activate against the current
// membership snapshot.
// WHITELABEL-STRICT-TENANT-SCOPE-1 — the host binding THIS tab runs under, in
// the shape the shared scopers take: `{ organizationId, workspaceId }` on a
// bound white-label host, `false` on a customer host whose tenant could not be
// established (the seed's verdict — no tenant renders), `null` on a platform
// host (every scoper is then the identity). Sources, in arrival order: the
// pre-mount seed (`root.whitelabelHostScope`, mirrored by the module pin), the
// pin alone (customDomainBoot's late write), the brand's ids (an entry that
// predates the seed). Pure; exported for its unit test.
export const whitelabelHostScopeForRoot = (root) => {
  const binding = root ? root.whitelabelHostScope : null
  const pinned = getHostScopeOrgId()
  if (binding && binding.organizationId && (!pinned || pinned === binding.organizationId)) {
    return binding
  }
  const brand = root ? root.whitelabelBrand : null
  const orgId = pinned || (brand && brand.organizationId) || null
  if (orgId) {
    const fromBinding = binding && binding.organizationId === orgId ? binding.workspaceId : ''
    const fromBrand = brand && brand.organizationId === orgId ? brand.workspaceId : ''
    return { organizationId: orgId, workspaceId: fromBinding || fromBrand || '' }
  }
  return binding === false ? false : null
}

// Exported for its unit test (auth.hostScopePin.test.js). The white-label
// stand-down below is a precedence rule with four inputs and no side effects,
// so it is worth asserting directly rather than through the whole auth tick.
export const resolveActiveOrgId = (root, orgsList, mongoActiveOrgId, govActiveOrgId) => {
  const list = Array.isArray(orgsList) ? orgsList : []
  const has = (id) => !!id && !!list.find((o) => o.id === id)

  const pending = root?._pendingActiveOrgId
  if (has(pending)) return pending

  // Multi-workspace-tabs: a tab's URL `/w/<org-slug>` PINS its org — it wins over
  // the Mongo/claim active org so no auth tick reverts a URL-scoped tab off the
  // org it's showing (I6). After the optimistic switch pick (which the switcher's
  // URL rewrite converges on anyway). No-op when the flag is off (no `/w/` URLs).
  if (shellIsMwtEnabled() && typeof window !== 'undefined') {
    const slug = shellGetWorkspacePrefixSlug(window.location?.pathname || '')
    const urlOrg = slug && list.find((o) => o.slug === slug)
    if (urlOrg && has(urlOrg.id)) return urlOrg.id
  }

  // A WHITE-LABEL HOST PINS ITS ORG, and auth stands down
  // (WHITELABEL-HOST-KEEPS-ACTIVE-WORKSPACE-1, the switchOrg race).
  //
  // Same principle as the MWT branch directly above, and deliberately the SAME
  // SLOT in the ladder: a pin that names this tab's tenant outranks the ambient
  // stored choice (Mongo/claims), but never the user's explicit in-tab switch
  // (`_pendingActiveOrgId`, handled first). The customer's own domain states
  // the tenant just as authoritatively as a `/w/<org>` URL does — it simply
  // states it through the hostname, which is why the MWT branch could not
  // cover it (that path is inert with the flag off, which is the common case
  // on these hosts).
  //
  // This is what makes the boot's `sdk.switchOrg` pin STICK. Without it, this
  // resolver kept returning the my.symbols active org, the drift-correction
  // block below it then re-minted the token to that org, and the two
  // un-awaited writes raced — 1 wrong of 5 loads measured on work.astio.ai
  // even after the structural fix. Standing down here means the wrong org is
  // never written at all, rather than written and then corrected, which
  // matters because a briefly-visible wrong tenant on a customer's branded
  // domain IS the defect.
  //
  // `has()` still gates it: a host naming an org this user does not belong to
  // falls through to the normal ladder instead of ghost-activating.
  const hostOrg = getHostScopeOrgId()
  if (has(hostOrg)) return hostOrg

  if (has(mongoActiveOrgId)) return mongoActiveOrgId

  if (has(govActiveOrgId)) return govActiveOrgId

  // Last rung: the first org a list OFFERS, not orgs[0] — /auth/me lists the
  // empty personal org first (POB r5; shared functions/visibleOrgs.js). Twin
  // of shared bootResolve.js resolveAuthSnapshot: change both or neither.
  return _wsShared.functions.visibleOrgs(list)[0]?.id || null
}

// A TRUSTWORTHY membership list = a non-empty `user.organizations` (the canonical
// Mongo membership set — present for federated AND sdk_only sessions). The boot
// org-persist (app.js) + driftHeal gate on this: when it's absent, orgsList fell
// back to the racing gov-only claims, so the resolved org may be an orgs[0] guess
// — persisting that rotates the active org across reloads.
export const hasTrustedOrgList = (user) =>
  Array.isArray(user?.organizations) && user.organizations.length > 0




const _matchesAuthPath = (path, authPaths) => {
  for (const ap of authPaths) {
    if (path === ap || path.startsWith(ap + '/') || path.startsWith(ap + '?')) return true
  }
  return false
}

// Canonical SDK token key list. Swept on stale-token detection so the
// next fresh sign-in starts from a clean slate. Intentionally broad:
// a key we clear unnecessarily only costs one extra sign-in; a key we
// leave behind risks the redirect loop indefinitely.
const _SDK_TOKEN_KEYS = [
  'symbols_access_token',
  'symbols_refresh_token',
  'symbols_expires_at',
  'symbols_user',
  'symbols_organization',
  'symbols_workspace',
  'gov_access_token',
  'symbols_session'
]

// Defensive stale-token sweep. Runs ONLY when we are about to bounce an
// unauthed user to /signin AND `symbols_access_token` is present in
// localStorage — which means hasSessionToken() already returned false
// (either exp is past, or exp is missing/zero and the token is degenerate).
//
// The combination "token key present but hasSessionToken false" is the
// dirty-token state that causes the redirect loop: the user lands on
// /signin, redirectIfUnauthed sees hasSessionToken() returns false (expired)
// so it lets them through — but then something else in the boot chain reads
// the raw `symbols_access_token` value (non-expired shape but zero exp),
// treats the user as authed, and bounces them back to /. Clearing here
// breaks the loop.
//
// Does NOT run on the happy path:
//   - cleanly authed user  → hasSessionToken() = true  → we never reach here
//   - cleanly unauthed     → no SDK token at all        → nothing to clear
const _clearStaleTokens = () => {
  if (typeof window === 'undefined') return
  try {
    const sym = window.localStorage.getItem('symbols_access_token')
    if (!sym) return // nothing to clear — cleanly unauthed
    const exp = +window.localStorage.getItem('symbols_expires_at')
    // Token is stale when exp is missing (0 after +null), zero, or in the past.
    if (exp && exp > Date.now()) return // token is valid — leave it alone
    // Must mirror hasSessionToken's refresh-token exemption. Without it this
    // sweep wipes the refresh token that makes an expired access token
    // recoverable — re-breaking the >1h-idle reload the exemption just fixed,
    // one layer down.
    if (window.localStorage.getItem('symbols_refresh_token')) return
    console.warn('[redirectIfUnauthed] clearing stale tokens before /signin bounce')
    for (const key of _SDK_TOKEN_KEYS) {
      window.localStorage.removeItem(key)
    }
  } catch (_) {
    /* localStorage may throw in sandboxed contexts — safe to swallow */
  }
}

// Bidirectional auth route gate. Runs at module-load time (before any
// page renders) so the redirect happens during the initial parse, not
// after the component tree has mounted:
//
//   - Unauthed user on a protected route  → `loginPath` (default /login)
//   - Authed user on ANY auth route       → `homePath` (default /)
//
// `publicPathPrefixes` wins — paths matching those prefixes render for
// both authed and unauthed users (e.g. `/user/:slug`, `/meet-guest`).
//
// Function name kept as `redirectIfUnauthed` for backwards compat with
// callers in `workspace-project/app.js`; the symmetric "auth user on
// /signin → bounce home" path is now part of the same guard.
//
// Returns `true` when it issued a `window.location.replace` (a hard redirect is
// in flight), `false` otherwise. The shell entry (index.js) uses this to SKIP
// bootShell on a doomed page — without the guard, bootShell ran its full
// SDK-init + getMe round-trip AND its own soft `replaceState('/login')` on the
// page that's already bouncing to /demo, racing the redirect (the demo's
// "/ → flash /login → /demo" multi-bounce) and burning a throwaway boot.
// Success-side reset for the login-bounce circuit breaker above: called by
// workspace/index.js after bootShell resolves an AUTHED shell, so the
// counter only ever accumulates across UNINTERRUPTED machine loops — a
// human who legitimately touches /login several times inside the 5-minute
// window has each visit separated by a successful boot, which zeroes it.
export const clearLoginBounceCount = () => {
  try {
    window.sessionStorage.removeItem('symbols_login_bounce')
  } catch (_) {
    /* sandboxed context */
  }
}

// GESTURE-GATED reset (2026-09-02, Nika: "/login?next=… screen still often
// causes loop and tab freeze"). Clearing directly on every authed boot left
// a structural hole: a loop whose cycle CONTAINS a successful boot — boot
// OK, then a post-boot guard bounces back to /login — zeroed the counter on
// every lap, so the breaker could never trip and the renderer eventually
// froze. The one thing a machine loop can never produce is a trusted user
// gesture, so the reset now arms on boot and fires on the first real
// pointerdown/keydown instead. A human clears it within seconds of using
// the app; an unattended looping tab accumulates counts and trips the
// breaker on the guard side. One-shot: the listeners remove themselves on
// first fire (whichever comes first).
export const armLoginBounceClearOnGesture = () => {
  if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return
  const onGesture = (e) => {
    if (e && e.isTrusted === false) return
    window.removeEventListener('pointerdown', onGesture, true)
    window.removeEventListener('keydown', onGesture, true)
    clearLoginBounceCount()
  }
  window.addEventListener('pointerdown', onGesture, true)
  window.addEventListener('keydown', onGesture, true)
}



export const redirectIfUnauthed = ({
  loginPath = '/login',
  homePath = '/',
  authPaths = AUTH_PATHS,
  publicPathPrefixes = [],
  isPublicPath = null
} = {}) => {
  if (typeof window === 'undefined') return false
  const path = window.location.pathname
  if (
    publicPathPrefixes.some(
      (p) => path === p || path.startsWith(p + '/') || path.startsWith(p + '?')
    ) ||
    (typeof isPublicPath === 'function' && isPublicPath(path))
  )
    return false
  const authed = hasSessionToken()
  const onAuthPage = _matchesAuthPath(path, authPaths)
  const target = authRedirectTarget({
    path,
    search: window.location.search || '',
    authed,
    demoHost: isDemoHost(),
    loginPath,
    authPaths,
    publicPathPrefixes,
    isPublicPath
  })
  if (target) {
    // Before bouncing, sweep any stale SDK token keys that could cause a
    // redirect loop on the next page load.
    _clearStaleTokens()
    window.location.replace(target)
    return true
  } else if (
    !authed &&
    onAuthPage &&
    isDemoHost() &&
    path !== '/demo' &&
    !path.startsWith('/demo/') &&
    !path.startsWith('/demo?')
  ) {
    // On a demo host the ONLY auth surface is the branded /demo email gate; the
    // real /login // signin pages are reachable only via the Claim modal (the
    // inline LoginWindow). A reauth bounce (bootShell _forceLogoutAndReauth →
    // full replace('/login') on a stale/GC'd demo session) or a stale link must
    // re-land the visitor on the email gate, never the signin page.
    _clearStaleTokens()
    window.location.replace('/demo')
    return true
  } else if (authed && onAuthPage) {
    // LOGIN-BOUNCE CIRCUIT BREAKER (2026-09-02) — renderer OOM crash at
    // /login?next=…: an expired-access + refresh-token pair reads as authed
    // here (hasSessionToken's recoverable-session exemption), the app boot
    // then loses the single-use refresh rotation (another tab of this origin
    // rotated it first — fleet worker tabs share the origin all day),
    // _forceLogoutAndReauth purges and bounces back to /login — and the
    // healthy tab's SDK re-writes fresh tokens before this tab's next guard
    // pass, so the forward fires again. Every hop re-evaluates the full dev
    // bundle in the SAME renderer; Chrome has no client-redirect loop
    // detection, so after enough rounds the renderer OOMs (four crash dumps,
    // 2026-09-01 21:11-21:20 + 2026-09-02 11:41, each matching worker
    // browser activity). sessionStorage is PER-TAB, so the counter sees only
    // this tab's own bounces. Three forwards inside 30s is a machine loop,
    // never a human: stop forwarding, drop this tab's token copy (it is loop
    // fuel — the healthy tab holds its session in memory and re-writes at
    // will), and fall through so the login form renders.
    try {
      const _bk = 'symbols_login_bounce'
      const _now = Date.now()
      let _b = null
      try {
        _b = JSON.parse(window.sessionStorage.getItem(_bk) || 'null')
      } catch (_) {}
      // WINDOW = 5 MINUTES, not 30s (2026-09-02, Nika: "sometimes still
      // goes 'Aw, snap'" — dumps at 13:18 + 13:58, AFTER the breaker
      // landed). Each loop cycle re-evaluates the full dev bundle TWICE
      // (login boot + app boot), which on this stack takes 30-40s — so
      // three forwards never fit inside a 30s window: the counter reset on
      // every pass and the "breaker" never tripped on exactly the slow
      // loops that OOM the renderer. Five minutes covers cycle times up to
      // ~100s with margin. False-positive safety: the counter is cleared
      // by bootShell on every SUCCESSFUL authed boot (see
      // clearLoginBounceCount), so a human legitimately landing on /login
      // a few times across five minutes never accumulates — only
      // uninterrupted machine loops do.
      if (!_b || typeof _b.t !== 'number' || _now - _b.t > 300000) _b = { n: 0, t: _now }
      _b.n += 1
      window.sessionStorage.setItem(_bk, JSON.stringify(_b))
      if (_b.n > 3) {
        console.warn(
          '[redirectIfUnauthed] login bounce loop broken after',
          _b.n - 1,
          "forwards in 5min — clearing this tab's tokens, showing the form"
        )
        for (const key of _SDK_TOKEN_KEYS) window.localStorage.removeItem(key)
        window.sessionStorage.removeItem(_bk)
        return false
      }
    } catch (_) {
      /* sessionStorage unavailable (sandboxed context) — keep old behavior */
    }
    // CROSS-ORIGIN HANDOFF FIRST (PREVIEW-AUTH-HANDOFF-NO-RETURN-LOCAL-1).
    // A consumer surface on ANOTHER origin (preview, gazer) that needs a
    // session bounces the user here as `/signin?redirect=<its own URL>`
    // (PreviewErrorBoundary._redirectToWorkspaceLogin → buildLoginRedirectUrl).
    // The two existing minters — signin.js's `onInit` and authGuard.js's
    // `maybeMintHandoffFromGuard` — both live BELOW this guard: this function
    // runs at module load in index.js, before any page mounts, and its home
    // bounce below is a HARD `window.location.replace`. So for an ALREADY
    // signed-in user the shell parked on its own home carrying a dead
    // `?redirect=` (measured: `/w/<org>/<ws>?redirect=<previewUrl>`) and the
    // consumer origin never received a token. Mint the one-shot fragment here
    // instead — the same payload, the same trust gate (`isTrustedHandoffTarget`
    // restricts targets to same-origin / same-plane shell + preview-app hosts /
    // — from a LOCAL shell only — localhost), so nothing new is exposed.
    //
    // AUTH-HANDOFF-NARROW-TARGETS: a target that renders a USER PROJECT (a
    // `<owner>--<key>` preview host) is not trusted here — this guard is
    // synchronous and runs before the SDK exists, so it cannot ask the
    // server. It returns false instead: the shell boots onto /signin and
    // signin.js `onInit` runs `confirmHandoffTarget` (project private AND
    // readable by this user) before it mints or sends the user home.
    //
    // Tokens come from localStorage rather than an SDK handle: this guard runs
    // before the SDK is constructed, and `hasSessionToken()` above already
    // proved the credential is live under these exact canonical keys.
    try {
      const redirectParam = new URLSearchParams(window.location.search || '').get('redirect')
      if (redirectParam && shellIsTrustedHandoffTarget(redirectParam)) {
        const accessToken =
          window.localStorage.getItem('symbols_access_token') ||
          window.localStorage.getItem('symbols_bridge_access_token')
        if (accessToken) {
          const payload = shellEncodeHandoffPayload({
            accessToken,
            refreshToken: window.localStorage.getItem('symbols_refresh_token') || '',
            expiresAt: parseInt(window.localStorage.getItem('symbols_expires_at') || '0', 10) || 0,
            expiresIn: parseInt(window.localStorage.getItem('symbols_expires_in') || '0', 10) || 0
          })
          window.location.replace(shellAppendHandoffFragment(redirectParam, payload))
          return true
        }
      }
      if (redirectParam && shellClassifyHandoffTarget(redirectParam).verdict === 'confirm') {
        return false
      }
    } catch (_) {
      /* malformed query / localStorage unavailable — fall through to the
         normal home bounce; the consumer surface still renders its
         signed-out state rather than a broken redirect. */
    }
    // Honour `?next=` — the auth guard encodes the route it bounced away from
    // there. Dropping it is why a bounced deep link came back to the dashboard
    // instead of the page that was asked for. `consumeOAuthNext` applies the
    // same-origin clamp the OAuth callback uses, so a hand-crafted
    // `?next=https://evil.com` can't turn this into an open redirect; it
    // returns '/' for anything unsafe, which we treat as "no usable target".
    let target = homePath
    let usedNext = false
    try {
      const next = new URLSearchParams(window.location.search || '').get('next')
      if (next) {
        const safe = shellConsumeOAuthNext(next)
        if (safe && safe !== '/') {
          target = safe
          usedNext = true
        }
      }
    } catch (_) {
      /* malformed query — fall back to homePath */
    }
    // tickets/sonnet.md "auth-page redirects still drop the query, and
    // ?claim=credits is exactly what they drop" — measured live: a
    // signed-in visitor hitting `/signup?claim=credits` bounced to
    // `homePath` with `location.search` wiped. This is the same
    // bare-path-redirect class the router ticket fixed at the shell
    // boundary (opus.md, workspace 6d146d9a) — one more instance of it,
    // in the auth guard rather than a page component. Only applied when
    // `next` wasn't used: `safe` is already a fully-resolved destination
    // in its own right, and appending the OUTGOING location's query on
    // top of it would duplicate/garble whatever `next` itself encodes.
    if (!usedNext) target = withLiveQuery(target)
    window.location.replace(target)
    return true
  }
  return false
}

// Subscribe to auth state changes. Accepts two callbacks:
//   onSignIn(user)            — fired once per new user session (new id)
//   onWorkspaceChange(claims) — fired when active_workspace_id changes
//                               for the same user (e.g. org switch).
//                               NOT called on initial sign-in (onSignIn
//                               covers that path).
//
// Both are optional. Returns an unsubscribe function (sync — see the
// cancel-token shim below for the SDK-proxy reason).
//
// SDK's onAuthStateChange keeps the legacy (event, session) callback shape,
// so subscriber bodies read the same as they always did.
// String-normalize a workspace-id cursor before comparing. `nextActiveWs`
// comes straight off the JWT claim (`nextClaims.active_workspace_id`, line
// ~648) with NO normalization, and a routine `TOKEN_REFRESHED` re-mint can
// hand back the SAME logical workspace in a different literal form (Mongo
// ObjectId vs UUID string, incidental whitespace) — this file already
// documents the identical drift for the sibling `nextId`/`lastUserId` pair
// (see `_loadInFlight`'s comment above) and a prior fix (workspace ab5cef23,
// "PERF-1 vector 3") closed the BOOT-time instance of THIS exact branch by
// gating it on `_loadInFlight`. That guard only covers the narrow window
// while `onSignIn`'s `loadPrivateData` is still executing — a refresh that
// lands minutes into a live session (well past `_loadInFlight`, well past
// `isInitialClaimLanding`) was never covered, and the raw `!==` below fired
// a full `onWorkspaceChange` re-hydration for a workspace that never
// actually changed (RAIL-TRANSITION, tickets/fable.md — the View-as
// loader-spiral: a same-workspace format-drift tick raises the MainLoader,
// PHASE-1-wipes `workspaceProject` mid-render — while `_canvasSlot` still
// names a persona route, so the slot 404s until republish completes — and
// the storm was intermittent because it only fires when a background
// refresh tick's timing lands mid-session, never on the very first switch).
const _wsIdStr = (v) => (v == null ? '' : String(v).trim())

// The exact decision this file got wrong (RAIL-TRANSITION): "did the
// governance claim's active-workspace pointer name a DIFFERENT workspace,"
// not "is the raw string byte-identical." Exported + pure so it can be
// pinned in a unit test independent of `subscribeAuthSession`'s SDK/network
// plumbing (see workspaceClaimChanged.test.js).
export const workspaceClaimChanged = (nextActiveWs, lastActiveWorkspaceId) =>
  !!nextActiveWs && _wsIdStr(nextActiveWs) !== _wsIdStr(lastActiveWorkspaceId)

// Cross-org claims drift: which workspace should the tab converge on when the
// JWT's `active_workspace_id` points OUTSIDE the resolved active org? The
// tab's LIVE workspace is authoritative whenever it already belongs to the
// active org — URL-named `/w/<org>/<ws>` tabs run non-first workspaces by
// design, and substituting a list-order guess manufactured a drifted
// `nextActiveWs` on every claims tick, which `workspaceClaimChanged` above
// read as a REAL switch → full `onWorkspaceChange` re-hydration (MainLoader,
// `workspaceProject` wipe, slot "Not found") on ordinary gestures like
// opening the assistant. Exported + pure so it can be pinned in a unit test
// independent of `subscribeAuthSession`'s plumbing (same idiom as
// `workspaceClaimChanged`; see workspaceClaimChanged.test.js).
export const preferLiveWorkspaceOnDrift = (liveWs, fallbackWs, inActiveOrg) =>
  liveWs && liveWs.id && typeof inActiveOrg === 'function' && inActiveOrg(liveWs)
    ? liveWs
    : fallbackWs

export const subscribeAuthSession = (root, onSignIn, onWorkspaceChange) => {
  if (!root) return () => {}
  // See the import-site note above — root.call reaches the live singletons
  // (module-private WeakMap / dedup cache) instead of a raw cross-file import
  // frank cannot resolve once this function is serialized.
  const getWorkspaceCached = (...args) =>
    typeof root.call === 'function'
      ? root.call('getWorkspaceCached', ...args)
      : typeof _gwcDirect === 'function'
        ? _gwcDirect(...args)
        : Promise.resolve(null)
  const beginPostAuthWorkspaceReveal = (...args) =>
    typeof root.call === 'function'
      ? root.call('beginPostAuthWorkspaceReveal', ...args)
      : typeof _bpwrDirect === 'function'
        ? _bpwrDirect(...args)
        : null
  const endPostAuthWorkspaceReveal = (...args) =>
    typeof root.call === 'function'
      ? root.call('endPostAuthWorkspaceReveal', ...args)
      : typeof _epwrDirect === 'function'
        ? _epwrDirect(...args)
        : false
  // The session end (WS-SIGNIN-DOES-NOT-SUPERSEDE-INFLIGHT-SWITCH-1) reaches
  // the shell's registry through the root state's element, the way
  // workspaceConfigs.js reaches `applyWorkspaceLangPolicy`: the root STATE
  // carries no `.call`, its back-pointer element does. No sibling import: a
  // new one is a frank FA001 error, and a free identifier under frank
  // revival. With no element there (it is transiently absent while a switch
  // replaces the root state) the end is skipped: the behaviour before this
  // fix, never a throw.
  const endWorkspaceSession = (...args) => {
    const el = root.__element
    return typeof el?.call === 'function' ? el.call('endWorkspaceSession', ...args) : undefined
  }
  let lastUserId = null
  // The signed-in person's email, beside `lastUserId`: the same person can
  // come back under another id format (see `_loadInFlight`), never under
  // another email, so a session change compares this one.
  let lastUserEmail = null
  let lastActiveWorkspaceId = null
  let lastClaimsKey = null
  // Cache the last `activeOrgId` we wrote to Mongo via the drift-heal
  // below so TOKEN_REFRESHED replays + identical claim ticks don't
  // re-PATCH /auth/me/active-org. Reset on heal-failure so retries are
  // possible on the next tick.
  let lastHealedActiveOrgId = null
  // Guards the cross-org workspace token re-mint (switchOrg) below — fires
  // once per drifted active org per session, never in a loop. Reset on
  // sign-in / sign-out alongside lastHealedActiveOrgId.
  let _lastRemintedOrgId = null
  // Guards the SDK workspaces-list fallback below — one fetch per active org.
  // RE-ARMED on failure/empty (see the fallback's catch): this is the only
  // workspace-scope source for a claims-less session, so consuming it on a
  // transient blip left the whole session scope-less and the identity chrome
  // empty (my.symbols.app sign-in, 2026-07-30).
  let _wsListFetchedOrgId = null
  // One-shot watchdog: the identity chain (orgs → org → workspace) fails
  // SILENTLY by design — every fallback is a swallowed warn — which made
  // Nika's empty-chrome boot unattributable in telemetry. One error-level
  // breadcrumb when the chain is still unresolved well after sign-in gives
  // the analyzed pipeline something to catch.
  let _identityWatchdogArmed = false
  const _armIdentityWatchdog = (root) => {
    if (_identityWatchdogArmed) return
    _identityWatchdogArmed = true
    const t = setTimeout(() => {
      try {
        const orgsOk = Array.isArray(root.orgs) && root.orgs.length > 0
        const orgOk = !!(root.org && (root.org.id || root.org._id))
        const wsOk = !!(root.workspace && (root.workspace.id || root.workspace._id))
        if (root.signedIn && (!orgsOk || !orgOk || !wsOk)) {
          console.error('[auth] identity chain unresolved 10s after sign-in', {
            orgsOk,
            orgOk,
            wsOk
          })
        }
      } catch {
        /* watchdog must never throw */
      }
    }, 10000)
    if (t && typeof t.unref === 'function') t.unref()
  }
  // PERF-3 vector-2: in-flight dedup guard for loadPrivateData (called
  // from onSignIn). When a TOKEN_REFRESHED event fires while onSignIn is
  // still executing, the refreshed token may carry a user object whose
  // `u.id` differs in format from the original `u._id` (Mongo ObjectId
  // string vs the legacy external UUID). That makes `nextId !== lastUserId`
  // true even though the user
  // hasn't changed, and a second onSignIn call fires concurrently.
  // The in-flight guard is strictly additive to the _authStateDelivered
  // PERF-1 guard above (which suppresses the warm-boot double-fire);
  // this covers the TOKEN_REFRESHED race that PERF-1 cannot catch because
  // _authStateDelivered is already true by the time the refresh fires.
  // It holds the in-flight call's own token, so an old call's settle never
  // releases a newer one's latch, and a session change releases it (see the
  // session end below): the old person's onSignIn may run on, but the next
  // person's must fire (WS-SIGNIN-DOES-NOT-SUPERSEDE-INFLIGHT-SWITCH-1).
  let _loadInFlight = null
  // Post-sign-in workspace-MODULE (re)load. onSignIn (loadPrivateData) can fire
  // on the FIRST auth tick BEFORE the active workspace resolves — the SDK emits
  // onAuthStateChange the moment `symbols_access_token` lands, AHEAD of the
  // governance JWT that carries `active_workspace_id` + `workspaces[]` (see the
  // fresh-login race note in loadPrivateData.js). With no `root.workspace`,
  // loadWorkspaceProject no-ops, so the active workspace's MODULE (its pages /
  // nav / home-widgets) never injects — and because onSignIn only fires once per
  // user (`lastUserId` guard) and the initial claim-landing below only advanced
  // the cursor, the module used to load ONLY after a full page reload (which
  // boots with the workspace already resolved). When the workspace resolves on a
  // LATER tick we must re-run the module load. loadWorkspaceProject is idempotent
  // — monotonic `_loadSeq` (a newer load wins, older bails post-await) plus the
  // `_activeProjectId` dedup (same module never re-injects) — so a redundant call
  // over an already-loaded module is a cheap no-op, never a double-inject. Keyed
  // by workspace id so it fires at most once per workspace per session; org
  // switches go through onWorkspaceChange (which loads the module itself), never
  // this path. Reset on sign-out so a re-sign-in re-loads.
  let _moduleLoadedForWsId = null
  const _ensureWorkspaceModule = (wsId) => {
    if (!wsId || _moduleLoadedForWsId === wsId) {
      // Nothing left that could inject routes on this boot — no workspace in
      // scope (e.g. a stale `?ws=` handle from another org resolved to null),
      // or the module already loaded. Either way the latch MUST still settle:
      // this early return previously skipped it, leaving `_appRoutesSettled`
      // unset forever, which kept the '/*' NotFoundBody hidden — a genuinely
      // nonexistent path showed a blank page instead of its 404 (measured on
      // /w/natali/definitely-not-a-page with a mismatched ws pointer).
      root.update({ _appRoutesSettled: true }, { preventFetch: true })
      return
    }
    _moduleLoadedForWsId = wsId
    // The first auth event can precede the workspace claim/list. Once that
    // workspace lands, keep the original loader epoch alive through the
    // corrective module/apps/config AND dashboard-prefs reads. Otherwise the
    // shell reveals default chrome/layout, then repaints two or three times as
    // each late result lands.
    const reveal = beginPostAuthWorkspaceReveal(root, wsId)
    // Suppress the shell catch-all's "Not found" CONTENT while module/app
    // routes are still being injected. A hard load on an injected route (e.g.
    // /w/natali/crm) hits the router at BOOT, before these loaders have run,
    // so nothing matches and it falls through to '/*' — which rendered a
    // fully-styled 404 for the entire activation window (~12s measured).
    // `_rerouteIfStranded` already recovers once the pages exist; this only
    // stops the false 404 from being SHOWN meanwhile. Cleared on every exit
    // path (including failure) so a genuinely nonexistent path still 404s.
    // A "settled" LATCH, not a pending flag. A pending flag races: `_authReady`
    // flips true a tick before this function runs, so there was a window where
    // neither gate held and the 404 rendered anyway (measured). The latch has
    // no such gap — it is absent from boot and set exactly once, so the gate is
    // simply "token-bearing user, routes not injected yet".
    const _settle = () => root.update({ _appRoutesSettled: true }, { preventFetch: true })
    // The claim can arrive while a warm snapshot is still painted or while
    // its list-shaped workspace is fetching a full record. Do not let that
    // provisional record load a System/default module and then suppress the
    // full-record converge that would correct it. The entire corrective chain
    // is intentionally pinned to the claim's exact, authoritative workspace.
    // Boot fix step 2: the post-claim reveal waits for the takeover module and,
    // on a deep link, the one app whose page the URL names — never for apps
    // the page does not show. They keep setting up after the reveal.
    let _releaseModuleReveal = () => {}
    const moduleReveal = new Promise((resolve) => {
      _releaseModuleReveal = resolve
    })
    const moduleChain = Promise.resolve(awaitInitialWorkspace(root, { workspaceId: wsId }))
      .then(async (ready) => {
        if (!ready || !hasAuthoritativeWorkspace(root, wsId)) return false
        // `boot: true` — this chain is one of three boot entrants for the same
        // scope; a later entrant reuses a settled pass instead of re-running it.
        await loadWorkspaceProject(root, shellGetSDK(), { boot: true })
        const appsRun = loadWorkspaceApps(root, shellGetSDK(), { boot: true })
        whenAppRouteReady(root).then(() => {
          try {
            applyWorkspaceConfig(root)
          } catch (e) {
            console.warn('[auth] post-sign-in applyWorkspaceConfig (reveal) failed:', e?.message || e)
          }
          _releaseModuleReveal()
        })
        await appsRun
        applyWorkspaceConfig(root)
        return true
      })
      .then((loaded) => {
        // The bounded authority wait timed out or was superseded. Do not run
        // any workspace-scoped side effect against a provisional record; the
        // full-record listener / next auth tick will own a later retry.
        if (!loaded) {
          if (_moduleLoadedForWsId === wsId) _moduleLoadedForWsId = null
          return
        }
        // The chain RESOLVED, but either loader may have settled only
        // TRANSIENTLY: 'unresolved' (workspace/org scope wasn't determinable
        // on that pass — the cold-boot convergence window) or 'error' (a
        // referenced module's / every installed app's fetch failed). The
        // loaders schedule their own converge retry for those; ALSO re-arm
        // this latch so a later auth tick can drive a fresh load if that
        // bounded retry window closes first. Definitive outcomes ('active' /
        // 'no-module' / 'refused' / 'empty') keep the latch — those must not
        // re-load on every tick. BOTH outcomes are consulted: a workspace
        // running only additive apps is definitively 'no-module' on the
        // takeover side even when 0 of its N installed apps loaded (the
        // natali empty-dashboard bug) — the apps outcome is what says so.
        const outcome = getModuleLoadOutcome()
        const appsOutcome = getAppsLoadOutcome()
        const transient =
          outcome === 'unresolved' ||
          outcome === 'error' ||
          appsOutcome === 'unresolved' ||
          appsOutcome === 'partial' ||
          appsOutcome === 'error'
        // Settle the 404-suppression latch only on definitive passes. A
        // transient pass keeps it unset so the '/*' catch-all stays covered
        // while the converge retry re-runs the loaders — the retry (or its
        // bounded give-up) settles it.
        if (!transient) _settle()
        if (transient && _moduleLoadedForWsId === wsId) {
          _moduleLoadedForWsId = null
        }
      })
      .catch((err) => {
        _settle()
        // Allow a retry on a later tick if the load genuinely failed.
        if (_moduleLoadedForWsId === wsId) _moduleLoadedForWsId = null
        console.warn('[auth] post-sign-in workspace module load failed:', err?.message || err)
      })
    // Re-run the home-dashboard-prefs GET (grid_layout / hidden_widgets),
    // explicitly pinned to `wsId` — the workspace this function just resolved.
    // loadPrivateData's OWN `_prefsChain` already ran once on the FIRST auth
    // tick (see the block comment above), and on THAT tick `root.workspace` was
    // frequently still unset (or, with a stale/foreign `?ws=` URL handle, briefly
    // resolved to the WRONG workspace — see workspaceUrlSync.js / bootShell's
    // `resolveActiveWorkspace`) — so that first fetch pinned to 'default'/null/
    // the wrong workspace, or found nothing. Nothing else in the app ever
    // re-fetches `userDashboardPrefs` outside `loadPrivateData` (boot) and
    // `onWorkspaceChange` (org switch) — and THIS path (workspace resolves on a
    // LATER sign-in tick) is neither of those — so without this call the grid's
    // saved layout for the CORRECT workspace was never fetched for the rest of
    // the session: pages/main.js `_initGridstack` would boot (and permanently
    // stay) on whatever the wrong-scope/empty first read left behind, i.e. each
    // widget's raw manifest position instead of the user's saved arrangement
    // (2026-07-30 "widgets rearranged to different positions" — GreenIG). Pure
    // GET + `root.update`, no mutation, so firing it again here is always safe
    // even when the first fetch happened to already have the right scope.
    // This belongs to the same post-claim reveal transaction as the module
    // chain above. The route latch still settles independently, but the main
    // loader remains until the correct workspace's layout row has landed.
    // `write` refuses a stale write — mirrors `_savePrefsPatch`'s own
    // scope-still-current guard in pages/main.js: by the time this GET
    // resolves, a DIFFERENT workspace resolution may have already superseded
    // `wsId` (both callers of `_ensureWorkspaceModule` set `root.workspace`
    // to their target synchronously, in the same tick, right before calling
    // it — so a live mismatch here means something newer already won).
    const prefsChain = Promise.all(
      _dashboardPrefsFetches(root, shellGetSDK(), {
        tag: 'post-sign-in: ',
        workspaceId: wsId,
        write: (patch) => {
          if (root.workspace?.id && String(root.workspace.id) !== String(wsId)) return
          root.update(patch)
        }
      })
    ).catch((err) =>
      console.warn('[auth] post-sign-in dashboard-prefs fetch failed:', err?.message || err)
    )
    // Both branches swallow their own operational errors so an unavailable
    // backend cannot wedge the UI. The epoch guard in endDataLoad means a
    // newer org/workspace switch still owns its own loader/reveal.
    moduleChain.finally(() => _releaseModuleReveal())
    Promise.all([moduleReveal, prefsChain]).finally(() => endPostAuthWorkspaceReveal(root, reveal))
  }
  const handler = (_event, session) => {
    const u = session?.user
    // A user-less tick is AMBIGUOUS under Mongo-token auth: the SDK's
    // session mirrors its TokenManager, and `user` hydrates only via
    // getMe()/login()/boot() — a subscriber can receive `session.user:
    // null` while every REST call authenticates fine. Treating that as
    // sign-out wiped the bootShell-seeded identity (signedIn / orgs /
    // org / workspace → the recurring empty-chrome boot: org switcher
    // gone, no active-workspace session info). Real sign-outs clear
    // tokens BEFORE emitting SIGNED_OUT (AuthService.logout, both
    // paths), and token rejections force-logout in bootShell — both
    // reach here token-less and still tear down below. Token present +
    // no user → hold current state and wait for the hydrated tick
    // (USER_UPDATED from adoptSessionUser / getMe).
    if (!u && hasSessionToken()) return
    const nextEmail = u?.email || ''
    const nextId = u?.id || u?._id || ''
    // Canonical Mongo `User.username` — surfaced on root state so
    // surfaces that persist or route on a user-handle (community posts,
    // author chips, `/user/<slug>` deep links) can prefer the canonical
    // value over an email-prefix fallback. Without this, every consumer
    // ended up using `email.split('@')[0]` which silently diverged from
    // the user's real handle (e.g. nika@symbols.app → "nika" vs the
    // actual username "nikoloza", causing /user/nika to 404).
    const nextUsername = u?.username || ''
    // Legacy external auth UUID — carried on the Mongo user record
    // as `supabaseUserId` (nothing mints new ones; migrated rows key on it).
    // Surfaced on root state so host-only gates on rows keyed by
    // `auth.uid()` (meet_rooms.created_by, etc.) can match the active
    // user without the Mongo _id mismatch.
    const nextSupabaseId = u?.supabaseUserId || ''
    const nextSignedIn = !!u
    // A session change ends every pass of the old session before anything of
    // the next one starts (WS-SIGNIN-DOES-NOT-SUPERSEDE-INFLIGHT-SWITCH-1):
    // the signed-in person signs out (no user and no token: the hold above
    // returned on an ambiguous tick), or another person's session replaces
    // theirs. The cross-tab sign-out keeps this page and the next sign-in is
    // an SPA one, so an old switch's late answers would land in the next
    // person's root. The same person under another id format keeps the
    // session (same email); the first sign-in of a page has no old session.
    // The old onSignIn's latch goes with the session: the next person's
    // onSignIn below must fire while the old one still runs.
    if (
      lastUserId &&
      (!u ||
        (nextId &&
          nextId !== lastUserId &&
          !!lastUserEmail &&
          !!nextEmail &&
          String(lastUserEmail).toLowerCase() !== String(nextEmail).toLowerCase()))
    ) {
      endWorkspaceSession(root)
      _loadInFlight = null
    }
    // Org membership lives in Mongo on `user.organizations` (returned by
    // the SDK getSession / /core/auth/me) — that's the source of truth.
    // `app_metadata` claims (active_workspace_id, workspaces[],
    // workspace_org_id) are OPTIONAL enrichment; orgs still resolve when
    // they're absent so AppWorkspaceSwitcher renders the user's org list
    // either way.
    const mongoOrgs = Array.isArray(u?.organizations) ? u.organizations : null
    const nextClaims = { ...(u?.app_metadata || {}) }
    // Prefer Mongo's `user.organizations` when the legacy claims'
    // `app_metadata.orgs` is missing OR present-but-empty. The federation
    // bridge's mint can land an empty `orgs: []` when claims are synthesized
    // before OrgMember rows are joined — Mongo is the canonical membership
    // source, so an empty legacy claims array shouldn't shadow it. Without this
    // guard, AppWorkspaceSwitcher hides because `_orgs(root)` reads
    // `root.jwtClaims.orgs` (the federated value), even though
    // `root.orgs` (state-level, derived from Mongo) has real entries.
    const claimsHasOrgs = Array.isArray(nextClaims.orgs) && nextClaims.orgs.length > 0
    if (!claimsHasOrgs && Array.isArray(mongoOrgs) && mongoOrgs.length > 0) {
      nextClaims.orgs = mongoOrgs
    } else if (claimsHasOrgs && Array.isArray(mongoOrgs)) {
      // Drift defense: a legacy `app_metadata.orgs` claim
      // can carry entries the user is no longer a member of (server-side
      // bridge SERVER-AUTH-ORGS-DRIFT-1 has been fixed for new mints,
      // but EXISTING sessions keep their stale JWT until next sign-in).
      // Without this intersect, every consumer that fans out per-org
      // (loadAllTeams → listOrgMembers + listTeams, dashboard org tiles,
      // mention lookups) fires a request per stale org and the browser
      // logs a 403 per request — a multi-second console wall on every
      // page load. Mongo's `user.organizations` is the canonical source
      // (derived from `OrgMember.find({user})` server-side); intersect
      // against it to drop drift entries before any downstream consumer
      // sees them. Drops zero real memberships — if an org is in Mongo
      // elsewhere, it'd already be missing from `nextClaims.orgs`
      // and the missing-fallback branch above would have used Mongo.
      const mongoIds = new Set(mongoOrgs.map((o) => String(o?.id || o?._id || '')).filter(Boolean))
      const filtered = nextClaims.orgs.filter((o) => mongoIds.has(String(o?.id || o?._id || '')))
      if (filtered.length !== nextClaims.orgs.length) {
        const dropped = nextClaims.orgs
          .map((o) => String(o?.id || o?._id || ''))
          .filter((id) => id && !mongoIds.has(id))
        console.warn(
          '[auth] dropped stale orgs from JWT claims (federation drift):',
          dropped,
          '— resign in to mint a fresh JWT and clear this warning permanently.'
        )
      }
      // Backfill Mongo-only orgs (governance bridge narrows app_metadata.orgs
      // to team-only before minting the JWT, so personal orgs never reach
      // claims even though the user owns them server-side). Union the
      // drift-filtered JWT orgs with any Mongo entries the bridge stripped —
      // canonical membership lives in OrgMember, so anything Mongo has is
      // legitimate. Without this, AppWorkspaceSwitcher / NoOrgOverlay /
      // every other consumer reading `root.orgs` only sees team orgs and
      // personal-org users are invisible to the workspace UI.
      const jwtIds = new Set(filtered.map((o) => String(o?.id || o?._id || '')).filter(Boolean))
      const mongoOnly = mongoOrgs.filter((o) => !jwtIds.has(String(o?.id || o?._id || '')))
      nextClaims.orgs = mongoOnly.length ? [...filtered, ...mongoOnly] : filtered
    }
    // localStorage.activeWorkspace is the user's explicit choice + the canonical
    // pointer. The JWT's active_workspace_id is NOT re-minted on a workspace
    // switch (nor when switching to a brand-new workspace not yet in
    // claims.workspaces), so it goes stale and would otherwise win here and
    // overwrite the user's selection on every auth tick. Override the stale
    // claim with the LS pointer BEFORE deriving nextActiveWs / claimsKey /
    // activeWorkspace, so the whole resolution (and the persisted claimsKey
    // change-detection) reflects the chosen workspace. Mirrors the same
    // override in bootShell `_resolveAuthSnapshot` — keep the two in sync.
    try {
      const _lsAws = shellGetActiveWorkspacePointer() || ''
      if (_lsAws && String(_lsAws) !== String(nextClaims.active_workspace_id)) {
        nextClaims.active_workspace_id = _lsAws
      }
    } catch (_) {}
    // `let` — the cross-org convergence below re-stamps this when the claimed
    // workspace turns out to belong to another org, so the sign-in /
    // workspace-change cursors record the CONVERGED id (recording the foreign
    // one made the next tick's comparison fire a spurious full reload).
    let nextActiveWs = nextClaims?.active_workspace_id || ''
    const userChanged =
      root.signedIn !== nextSignedIn ||
      root.currentUserEmail !== nextEmail ||
      root.currentUserId !== nextId ||
      root.currentUserUsername !== nextUsername ||
      root.currentUserSupabaseId !== nextSupabaseId
    // Full-payload diff so TOKEN_REFRESHED ticks that re-mint an
    // identical claim set are no-ops, but ANY claim change (orgs,
    // permissions, workspaces, tier, role, ...) still propagates.
    // Stringify is ~50µs on a 2kB JWT vs the cost of stale role/perm
    // gates — correctness wins.
    const claimsKey = JSON.stringify(nextClaims)
    const claimsChanged = claimsKey !== lastClaimsKey
    // Derive shell-scope state from claims so the OrgSwitcher / shell chrome
    // doesn't have to wait on a separate `/orgs` round-trip — the JWT
    // already carries the user's org membership + active workspace.
    // WS-WHITELABEL-TENANT-SCOPE-1 (auth-tick half) — narrow the CLAIMS to the
    // host's tenant before anything is derived from them.
    //
    // This tick rebuilds `nextClaims.orgs` from Mongo (above) and then writes
    // `{ orgs, workspaces, org, jwtClaims }` to root in ONE update. That
    // overwrote the scoped BOOT snapshot with the account-wide list: measured
    // on gita.pr.symbols.app after the boot fix shipped, `root.workspace` was
    // correctly GITA while `root.orgs.length` was 17 and
    // `jwtClaims.active_workspace_id` still named another org's workspace.
    //
    // Scoping HERE, before `orgsList` / `workspacesAll` / `activeOrgId` /
    // `activeOrg` are derived, covers all four plus the `jwtClaims` write with
    // one intervention that cannot drift apart. `resolveActiveOrgId` already
    // honours the host pin for WHICH org is active; this narrows WHICH ORGS
    // EXIST, which is the half the switcher reads.
    //
    // Source of the pin: `getHostScopeOrgId()` is canonical and fails closed.
    // WHITELABEL-STRICT-TENANT-SCOPE-1: it is now set PRE-MOUNT by
    // seedWhitelabelHostScope (whitelabelBrand.js), independent of branding,
    // with the same fact mirrored on `root.whitelabelHostScope`; the brand's
    // ids stay the last fallback for an entry that predates that seed. The
    // old sources were the brand (null for an UNBRANDED host — Unreal's org
    // has `branding: {}`) and customDomainBoot (which skipped /studio and
    // every deep link), so on unreal.pr.symbols.app this block never ran and
    // the tick resolved the account's stored org (Keepz).
    // `root.whitelabelHostScope === false` is the seed's "this customer host
    // could not prove its tenant" verdict: scope the claims to NO org.
    //
    // Written back onto `nextClaims` FIELD BY FIELD rather than rebound: the
    // binding is `const` (761) and this file already states its claim changes
    // by mutation (771, 809), so this matches the surrounding contract instead
    // of introducing a second one.
    const _hostScope = whitelabelHostScopeForRoot(root)
    if (_hostScope !== null) {
      const _scopedClaims = shellScopeClaimsToWhitelabelHost(nextClaims, _hostScope)
      if (_scopedClaims !== nextClaims) {
        if (Array.isArray(_scopedClaims.orgs)) nextClaims.orgs = _scopedClaims.orgs
        if (Array.isArray(_scopedClaims.workspaces)) {
          nextClaims.workspaces = _scopedClaims.workspaces
        }
        if (_scopedClaims.active_workspace_id) {
          nextClaims.active_workspace_id = _scopedClaims.active_workspace_id
        }
        if (_scopedClaims.workspace_org_id) {
          nextClaims.workspace_org_id = _scopedClaims.workspace_org_id
        }
      }
    }
    const orgsList = Array.isArray(nextClaims.orgs) ? nextClaims.orgs : []
    const workspacesAll = Array.isArray(nextClaims.workspaces) ? nextClaims.workspaces : []
    // List-shaped (JWT claims row — no `settings`). THIS is the exact object
    // that used to be handed straight to `root.update({ workspace: ... })`
    // below and silently downgrade a full record bootShell had already
    // resolved for the SAME id — the defect
    // tickets/spec-workspace-record-shape.md fixes. Stamped so the guarded
    // setter at the bottom of this tick can tell it apart from a complete
    // record; committing it now goes through `setActiveWorkspaceRecord`
    // instead of a bare `root.update`.
    let activeWorkspace = shellStampWorkspaceShape(
      workspacesAll.find((w) => w.id === nextActiveWs) || null,
      'list'
    )
    // When claims carry no workspaces (SDK-only session, gov JWT not minted),
    // keep whatever the SDK workspaces-list fallback below already resolved —
    // otherwise a later claims tick overwrites root.workspace back to null.
    // BUT only when root.workspace agrees with the chosen pointer (nextActiveWs,
    // LS-overridden above). On the first auth tick the governance claims haven't
    // loaded (workspacesAll empty) and root.workspace is still the Phase-1 seed
    // (the stale JWT workspace); adopting it here would resolve activeWorkspace
    // to the WRONG workspace, suppress the listWorkspaces fallback, and mirror
    // the stale id back to localStorage — silently reverting a switch to a
    // freshly-created workspace on refresh. When they disagree, leave it null so
    // the fallback fetches the org's real list and resolves the chosen one.
    if (
      !activeWorkspace &&
      !workspacesAll.length &&
      root.workspace?.id &&
      (!nextActiveWs || String(root.workspace.id) === String(nextActiveWs))
    ) {
      activeWorkspace = root.workspace
    }
    // The chosen workspace (nextActiveWs, now LS-overridden) may not be in the
    // JWT's claims.workspaces (a freshly-created workspace). bootShell's
    // pre-mount getWorkspace already resolved it into root.workspace, so PRESERVE
    // that here instead of nulling root.workspace on this tick (the SDK
    // workspaces-list fallback below still fills it when root.workspace lags).
    if (
      !activeWorkspace &&
      root.workspace?.id &&
      String(root.workspace.id) === String(nextActiveWs)
    ) {
      activeWorkspace = root.workspace
    }
    // Active org id resolves through the layered chain (pending → persisted
    // → governance → orgs[0]); see `resolveActiveOrgId` for full rationale.
    // We mirror the resolved id back into `nextClaims.workspace_org_id` so
    // every downstream consumer that already reads that field
    // (`filterToActiveOrg`, `_loadRoles`, etc.) re-keys without needing to
    // know about the localStorage path. Governance-derived value still
    // wins when present because the resolver checks it after the user-pick
    // layers but before the orgs[0] fallback.
    const govActiveOrgId = activeWorkspace?.org_id || nextClaims.workspace_org_id || null
    const mongoActiveOrgId = u?.activeOrganization ? String(u.activeOrganization) : null
    const activeOrgId = resolveActiveOrgId(root, orgsList, mongoActiveOrgId, govActiveOrgId)
    if (activeOrgId) nextClaims.workspace_org_id = activeOrgId
    const activeOrg = activeOrgId ? orgsList.find((o) => o.id === activeOrgId) : null
    // Cross-org workspace drift defense — `nextClaims.active_workspace_id`
    // and `nextClaims.workspaces` are JWT-scoped, NOT re-minted on every
    // `sdk.setActiveOrganization`. Mongo's `User.activeOrganization`
    // updates first; the JWT lags until refresh. In that window the
    // JWT's `active_workspace_id` can point at a workspace IN THE
    // PREVIOUS ORG, and `claims.workspaces` carries workspaces from
    // every org the user has ever touched. Surfacing either under the
    // resolved active org leaks foreign-org data into every consumer
    // that scopes by `workspace.id` (loadPrivateData,
    // _activeWorkspaceProjects, the workspace switcher, …). Scope
    // `workspaces` to the active org, and re-resolve activeWorkspace
    // against the scoped list; fall back to its first entry when the
    // JWT pointer is stale.
    const inActiveOrg = (w) =>
      !activeOrgId ||
      (w && String(w.org_id || w.organization_id || w.organization) === String(activeOrgId))
    let workspacesList = shellStampWorkspaceListShape(
      activeOrgId ? workspacesAll.filter(inActiveOrg) : workspacesAll
    )
    // `claims.workspaces` exists ONLY in the governance JWT, so an SDK-only
    // session (OAuth, or any session before federation lands) carries NONE and
    // this list is routinely empty — and writing it into root wiped the real
    // list boot resolved from the SDK, which is why the workspace switcher
    // reported "no workspaces to load" and why the URL could not name a
    // workspace. An empty claims list is ABSENCE OF INFORMATION, not evidence
    // that the user has no workspaces: keep what root already holds. Mirrors
    // the same preserve-don't-clobber rule already applied to activeWorkspace.
    if (!workspacesList.length && Array.isArray(root.workspaces) && root.workspaces.length) {
      workspacesList = activeOrgId ? root.workspaces.filter(inActiveOrg) : root.workspaces
    }
    if (activeWorkspace && !inActiveOrg(activeWorkspace)) {
      // Same preserve-don't-clobber rule as the two root.workspace branches
      // above; the live record keeps its own `_shape` provenance. A live
      // workspace that is itself foreign (genuine cross-org switch mid-
      // flight) fails `inActiveOrg` and still falls back to the list guess.
      // Rationale + reboot post-mortem on `preferLiveWorkspaceOnDrift`.
      activeWorkspace = preferLiveWorkspaceOnDrift(
        root.workspace,
        shellStampWorkspaceShape(workspacesList[0] || null, 'list', { clone: false }),
        inActiveOrg
      )
      // ONE identity: the claim mirror must follow the workspace this tab just
      // RESOLVED. Leaving the foreign id in `active_workspace_id` kept every
      // claim-reading consumer (fetch scope, chat scoping, dashboard-prefs
      // pinning) on the OTHER org's workspace even though root.workspace was
      // already corrected — the /w/<slug> cross-tenant dashboard leak
      // (2026-07-27). nextActiveWs re-stamps too so the cursors below record
      // the converged id, not the drifted one.
      if (activeWorkspace?.id) {
        nextClaims.active_workspace_id = String(activeWorkspace.id)
        nextActiveWs = String(activeWorkspace.id)
      }
      // We fixed the CLIENT `workspace` above, but the JWT still carries an
      // `active_workspace_id` from a workspace in the PREVIOUS org. Server-side
      // org-scoped fetches that ignore client args resolve the org FROM THE
      // TOKEN — most importantly `users.members.list` (loadPrivateData's people
      // list) — so they keep returning the wrong org's members, leaving /people
      // and the team-and-agents canvas empty. setActiveOrganization does NOT
      // re-mint the token; switchOrg does. Guarded so it fires once per drifted
      // org (after the re-mint the token's workspace is in-org and this branch
      // stops matching → no loop).
      if (activeOrgId && nextSignedIn && _lastRemintedOrgId !== activeOrgId) {
        _lastRemintedOrgId = activeOrgId
        const _sdk = shellGetSDK()
        if (_sdk && typeof _sdk.switchOrg === 'function') {
          // WHITELABEL-STRICT-TENANT-SCOPE-1 — on a white-label host the
          // re-mint is THIS tab's lens onto the host's tenant: never persist it
          // to the account's stored active org (switchOrg PATCHes Mongo unless
          // told not to), or one visit to a customer's domain moves the
          // account's my.symbols tabs onto that customer's workspace.
          Promise.resolve(
            isCustomDomainHost()
              ? _sdk.switchOrg(activeOrgId, { skipPersist: true })
              : _sdk.switchOrg(activeOrgId)
          ).catch((err) => {
            console.warn('[auth] cross-org workspace token re-mint failed:', err?.message || err)
          })
        }
      }
    }
    if (nextSignedIn) _armIdentityWatchdog(root)
    // SDK-only sessions (no governance JWT yet) carry ZERO workspaces in
    // claims → activeWorkspace stays null and every workspace-scoped read
    // (tickets list/counts/…) 400s with workspace_id_required, rendering
    // boards/feeds empty. Resolve the active org's workspaces straight from
    // the SDK once per org and seed root — the next tick's mirror block then
    // propagates the scope to the SDK context + storage.
    //
    // WS-AUTH-TICK-EMPTIES-WORKSPACES-1 — `!workspacesList.length` joined the
    // trigger: with claims carrying no list, the scoped derivation above can
    // legitimately come up EMPTY while the active WORKSPACE still resolves.
    // Branded host: boot seeds the MONGO active org's list, the host pin
    // scopes this tick to the brand org, `inActiveOrg` drops every row — and
    // the update below then writes `workspaces: []`. Gated on
    // `!activeWorkspace` alone, this fetch never ran (the workspace HAD
    // resolved), so nothing ever refilled the list: measured on
    // gita.pr.symbols.app — length 1 at boot, 0 after the tick, stable.
    // When the workspace is already resolved the fetch runs in LIST-ONLY
    // mode (`_wsListOnly`): it may fill `root.workspaces`, never converge
    // pointers — repointing the session because a LIST fetch answered would
    // let a list-membership blip switch the user's workspace.
    const _wsListOnly = !!activeWorkspace
    if (
      (!activeWorkspace || !workspacesList.length) &&
      nextSignedIn &&
      activeOrgId &&
      _wsListFetchedOrgId !== activeOrgId
    ) {
      _wsListFetchedOrgId = activeOrgId
      // Claim cursor at fetch time — the async resolve below compares against
      // it so a REAL switch that happened while the list was in flight is
      // never overwritten.
      const _tickWs = nextActiveWs
      const _sdkW = shellGetSDK()
      if (_sdkW && typeof _sdkW.listWorkspaces === 'function') {
        Promise.resolve(_sdkW.listWorkspaces({ organization: activeOrgId }))
          .then((res) => {
            // Server shape: { data: { items, total, page, limit } } — the SDK
            // may pre-unwrap `data`, so resolve both forms.
            const rows =
              (res && (res.items || res.data?.items || res.data || res.workspaces || res)) || []
            // WHITELABEL-STRICT-TENANT-SCOPE-1 — the ORG's list, narrowed to
            // the bound host's ONE workspace (identity on platform hosts), so
            // this fallback can never seed or converge onto a sibling
            // workspace the host does not serve.
            const list = shellScopeWorkspaceListToWhitelabelHost(
              (Array.isArray(rows) ? rows : [])
                .map((w) => ({
                  id: String(w._id || w.id || ''),
                  // `displayName` FIRST — see workspaceDisplayName's own
                  // comment in workspace-shared/functions/activeWorkspaceRecord.js.
                  name: shellWorkspaceDisplayName(w),
                  slug: w.slug,
                  type: w.type,
                  org_id: String(w.organization || activeOrgId),
                  role: w.role || 'member',
                  // List row — no `settings`. See
                  // tickets/spec-workspace-record-shape.md.
                  _shape: 'list'
                }))
                .filter((w) => w.id),
              whitelabelHostScopeForRoot(root)
            )
            if (!list.length) {
              // Re-arm on a transient empty answer. This fallback is the ONLY
              // workspace-scope source for a claims-less session (Supabase
              // claims are gone server-side) — consuming the per-org latch on
              // a blip left the session permanently scope-less: no
              // activeWorkspaceId ever reached the SDK, every workspace-scoped
              // read 400'd (workspace_id_required), and the identity chrome
              // (name/avatar/org switcher) rendered empty with zero errors —
              // Nika's 2026-07-30 my.symbols.app sign-in failure.
              if (_wsListFetchedOrgId === activeOrgId) _wsListFetchedOrgId = null
              return
            }
            if (_wsListOnly) {
              // The workspace was already resolved when this fetch was
              // dispatched — fill the LIST and stop. No pointer convergence,
              // no claim mirror, no setActiveWorkspaceRecord, no module load:
              // all of that describes the resolved WORKSPACE, which this
              // fetch knows nothing about. Guards: the scope may have moved
              // while the fetch was in flight (org switch, claims tick) —
              // write only while the fetched org is still the resolved one —
              // and never clobber a list a newer tick already filled.
              const _stillScoped =
                String(root.jwtClaims?.workspace_org_id || '') === String(activeOrgId)
              const _stillEmpty = !(Array.isArray(root.workspaces) && root.workspaces.length)
              if (_stillScoped && _stillEmpty) root.update({ workspaces: list })
              return
            }
            // Honor the chosen workspace (nextActiveWs — the LS pointer) when
            // it's in the freshly-fetched list; this is the path that resolves a
            // brand-new workspace missing from the JWT's claims.workspaces. Only
            // fall back to the org's hq / first when there's no valid choice.
            const hq =
              list.find((w) => String(w.id) === String(nextActiveWs)) ||
              list.find((w) => w.type === 'hq') ||
              list[0]
            // Mirror the scope IMMEDIATELY — the claims-tick mirror below only
            // runs on the next auth event, which may never come this session.
            try {
              _sdkW.updateContext?.({ activeWorkspaceId: hq.id })
              shellSetActiveWorkspacePointer(hq.id)
            } catch (err) {
              console.warn('[auth] workspaces fallback mirror failed:', err)
            }
            // Converge the CLAIM mirror + the workspace-change cursor to the
            // resolved workspace too. This async path runs outside any auth
            // tick, so without these the claim kept the drifted (possibly
            // foreign-org) id until the next token event — every claim-first
            // consumer (dashboard-prefs pinning, chat scoping, fetch scope)
            // read the wrong workspace for up to an hour, and the NEXT tick's
            // cursor comparison fired a spurious full workspace-change reload.
            const _claims = root.jwtClaims
            const _claimStale =
              _claims && String(_claims.active_workspace_id || '') !== String(hq.id)
            root.update({
              workspaces: list,
              ...(_claimStale
                ? { jwtClaims: { ..._claims, active_workspace_id: String(hq.id) } }
                : {})
            })
            // Guarded setter, not a bare `workspace: hq` in the update above —
            // `hq` is list-shaped; if a full record for this SAME id is
            // already held (e.g. this fallback re-arms after a transient
            // failure and bootShell's fetch already landed), the setter
            // refuses the downgrade and the auto-scheduled fetch below is a
            // no-op cache hit. See tickets/spec-workspace-record-shape.md.
            shellSetActiveWorkspaceRecord(root, hq, {
              reason: 'auth-tick:sdk-only-fallback',
              sdk: _sdkW,
              getWorkspaceCached
            })
            if (lastActiveWorkspaceId === _tickWs) lastActiveWorkspaceId = String(hq.id)
            // SDK-only sessions resolve their workspace HERE (no gov claims tick),
            // bypassing the initial-claim-landing branch below — so load the
            // module now too, else it'd wait for a manual reload just like the
            // federated path. Idempotent (see `_ensureWorkspaceModule`).
            _ensureWorkspaceModule(hq.id)
          })
          .catch((err) => {
            // Re-arm — same reasoning as the empty-list branch above: a failed
            // fetch must not permanently consume the only scope source.
            if (_wsListFetchedOrgId === activeOrgId) _wsListFetchedOrgId = null
            console.warn('[auth] SDK workspaces fallback failed:', err?.message || err)
          })
      }
    }
    // Normalize `active_workspace_id` across auth providers — mirrors the
    // backfill in bootShell `_resolveAuthSnapshot` (keep the two in sync).
    // Google/OAuth users land in the sdk_only state where the governance
    // claim is empty, so `nextClaims.active_workspace_id` is '' and the
    // home's `_wsScope` keys its dashboard cache to a DIFFERENT scope
    // ('default') than email/password users — same user, different home
    // (#auth-provider-home-diff). Backfill from the resolved workspace or
    // the canonical localStorage pointer so every provider resolves the
    // same scope. Done before claimsKey/mirror so the change propagates and
    // a later identical tick stays a no-op.
    if (!nextClaims.active_workspace_id) {
      let lsActiveWs = ''
      try {
        lsActiveWs = shellGetActiveWorkspacePointer() || ''
      } catch (_) {
        lsActiveWs = ''
      }
      const normalizedWs = (activeWorkspace && activeWorkspace.id) || lsActiveWs || ''
      if (normalizedWs) nextClaims.active_workspace_id = normalizedWs
    }
    // Mirror the resolved active workspace into the SDK's ambient context +
    // the persisted `activeWorkspace` storage key, so workspace-scoped reads
    // (tickets list/counts/resolutions — workspaceId REQUIRED since
    // server@ba9e0d8f) resolve their scope without every caller threading the
    // id. Light touch: updateContext only — NOT sdk.switchWorkspace (no
    // federation round-trip, no claims invalidation).
    if (activeWorkspace?.id) {
      const _sdkWs = shellGetSDK()
      if (_sdkWs && _sdkWs._context?.activeWorkspaceId !== activeWorkspace.id) {
        _sdkWs.updateContext?.({ activeWorkspaceId: activeWorkspace.id })
        try {
          shellSetActiveWorkspacePointer(activeWorkspace.id)
        } catch (err) {
          console.warn('[auth] activeWorkspace persist failed:', err)
        }
      }
    }
    // Drift self-heal: when the resolver picks an id different from
    // Mongo's `User.activeOrganization`, persist it. Without this,
    // Mongo stays stale (e.g. zachary@symbols.app pointing at a
    // personal org that has no governance backing → UI displays the
    // fallback `Symbols System` with a checkmark, but the server's
    // ReleaseController._getOrg / AnalyzedReadService._resolveWorkspaceId
    // both read Mongo and scope to the stale personal org → empty
    // page). The OrgSwitcher click path already calls
    // setActiveOrganization for explicit picks (`_pendingActiveOrgId`
    // matches), so skip in that case to avoid a redundant PATCH race.
    const usedPending = root?._pendingActiveOrgId && root._pendingActiveOrgId === activeOrgId
    // Require an actual Mongo user object before firing the PATCH — the
    // heal only makes sense when we KNOW we have a Mongo user record (the
    // thing whose `activeOrganization` we're trying to update). Without
    // this guard, a not-fully-authed tick (no /auth/me response yet, or
    // token rejected by the current env) would fire
    // PATCH /auth/me/active-org → 401 in the network console.
    const driftHeal =
      !!u &&
      nextSignedIn &&
      activeOrgId &&
      activeOrgId !== mongoActiveOrgId &&
      activeOrgId !== lastHealedActiveOrgId &&
      !usedPending &&
      // Only heal on a trustworthy membership list (never a gov-only fallback
      // guess). Still heals #2238 — the team org is in user.organizations.
      hasTrustedOrgList(u) &&
      // WHITELABEL-STRICT-TENANT-SCOPE-1 — a white-label host's org is the
      // HOST's binding, not a drift of the account's choice: "healing" it
      // would PATCH the stored active org to the customer's org from their
      // domain. The stored choice belongs to the platform host.
      !isCustomDomainHost()
    if (driftHeal) {
      // Mark BEFORE the async call so concurrent ticks don't double-fire.
      // On failure we deliberately do NOT clear this — a 403 / network
      // wedge would otherwise infinitely retry against every claim
      // tick. Sign-in / sign-out resets clear it for the legit retry
      // paths (different user, fresh session).
      lastHealedActiveOrgId = activeOrgId
      Promise.resolve(shellGetSDK().setActiveOrganization(activeOrgId)).catch((err) => {
        console.warn('[auth] active-org self-heal failed:', err?.message || err)
      })
    }
    // `workspace` is deliberately NOT in either `root.update` call below — it
    // goes through `setActiveWorkspaceRecord` instead, which is the ONLY
    // thing allowed to write `root.workspace` post-mount. A bare
    // `root.update({ workspace: activeWorkspace })` here is exactly the bug
    // tickets/spec-workspace-record-shape.md fixes: `activeWorkspace` is
    // frequently list-shaped (JWT claims row, no `settings`) and would
    // silently downgrade a full record bootShell already resolved for the
    // same id. The setter accepts `null` too (explicit sign-out / no-match
    // clear — same as the old unconditional overwrite in that case).
    //
    // WARM BOOT: while `_bootFromCache` is true the root holds the snapshot's
    // LIST placeholder and bootShell's `boot-warm-reconcile` is about to
    // write the composite boot's FULL record. Handing the setter the same
    // list row here started its list->full upgrade — one GET
    // /core/workspaces/:id for a record the boot already answered (measured
    // on dev deploy 27, BOOT-AFTER.md). Leave that write to the reconcile;
    // index.js converges whatever is still list-shaped once bootShell returns
    // (functions/convergeBootWorkspace.js). A DIFFERENT workspace, or a full
    // row, still goes through the setter.
    const _wsIdOf = (w) => (w ? String(w.id || w._id || '') : '')
    const _warmPlaceholderHeld =
      root._bootFromCache === true &&
      !!activeWorkspace &&
      activeWorkspace._shape !== 'full' &&
      !!root.workspace &&
      root.workspace._shape !== 'full' &&
      _wsIdOf(root.workspace) === _wsIdOf(activeWorkspace)
    if (userChanged) {
      root.update({
        signedIn: nextSignedIn,
        currentUserEmail: nextEmail,
        currentUserId: nextId,
        currentUserUsername: nextUsername,
        currentUserSupabaseId: nextSupabaseId,
        jwtClaims: nextClaims,
        orgs: orgsList,
        workspaces: workspacesList,
        org: activeOrg
      })
      if (!_warmPlaceholderHeld) shellSetActiveWorkspaceRecord(root, activeWorkspace, {
        reason: 'auth-tick:userChanged',
        sdk: shellGetSDK(),
        getWorkspaceCached
      })
      lastClaimsKey = claimsKey
    } else if (nextSignedIn && claimsChanged) {
      root.update({
        jwtClaims: nextClaims,
        orgs: orgsList,
        workspaces: workspacesList,
        org: activeOrg
      })
      if (!_warmPlaceholderHeld) shellSetActiveWorkspaceRecord(root, activeWorkspace, {
        reason: 'auth-tick:claimsChanged',
        sdk: shellGetSDK(),
        getWorkspaceCached
      })
      lastClaimsKey = claimsKey
    }
    if (nextId && nextId !== lastUserId) {
      // New sign-in: fire onSignIn and record the starting workspace.
      lastUserId = nextId
      lastUserEmail = nextEmail
      lastActiveWorkspaceId = nextActiveWs
      // Reset the drift-heal cache so a different user signing into
      // the same tab can heal their own activeOrganization on first tick.
      lastHealedActiveOrgId = null
      _lastRemintedOrgId = null
      if (typeof onSignIn === 'function' && !_loadInFlight) {
        const signInLoad = {}
        _loadInFlight = signInLoad
        // Carry the FULL session user through — the tick already holds it
        // (getSession / onAuthStateChange payload, no network), so consumers
        // like the telemetry identify don't have to re-fetch /auth/me for
        // traits the boot fetched seconds ago (a measured 1.7s duplicate
        // mid-boot, tickets/workspace.md boot ticket #3).
        Promise.resolve(onSignIn({ id: nextId, email: nextEmail, user: u || null })).finally(() => {
          if (_loadInFlight === signInLoad) _loadInFlight = null
        })
      }
    } else if (nextId && workspaceClaimChanged(nextActiveWs, lastActiveWorkspaceId)) {
      // Same user, workspace claim changed. Distinguish two cases:
      //   1. The governance bridge JWT landed AFTER `onSignIn` already
      //      ran (empty → 'X'). `loadPrivateData` is mid-flight against
      //      the same workspace, so a second pipeline here would
      //      duplicate every fetch (people, announcements, stories,
      //      loadAllTeams fan-out, …). Only update the cursor.
      //   2. The user actively switched orgs in another tab/device
      //      ('X' → 'Y'). That IS a re-hydration trigger — fire
      //      `onWorkspaceChange` so workspace-scoped tables refresh
      //      without a full page reload.
      const isInitialClaimLanding = !lastActiveWorkspaceId
      lastActiveWorkspaceId = nextActiveWs
      if (!isInitialClaimLanding && !_loadInFlight && typeof onWorkspaceChange === 'function') {
        onWorkspaceChange(nextClaims)
      } else if (isInitialClaimLanding) {
        // onSignIn (loadPrivateData) already ran with an EMPTY workspace (the
        // gov JWT hadn't landed yet), so its loadWorkspaceProject call no-op'd
        // and the workspace MODULE never injected. Now that the workspace claim
        // has landed and `root.workspace` is resolved (set by the root.update
        // above), load the module — the piece that previously required a manual
        // reload. Idempotent (see `_ensureWorkspaceModule`).
        _ensureWorkspaceModule(activeWorkspace?.id || nextActiveWs)
      } else if (_loadInFlight) {
        // The workspace claim CORRECTED ITSELF (cross-org drift swap above, a
        // stale ?ws=/pointer resolving, a re-mint landing) while the boot's
        // loadPrivateData is still mid-flight. The full onWorkspaceChange
        // re-hydration is deliberately suppressed (it would duplicate every
        // in-flight boot fetch) — but the MODULE load for the corrected
        // workspace must not be dropped with it: the boot chain already ran
        // loadWorkspaceProject against the PREVIOUS (wrong) workspace, the
        // cursor above has advanced, and no later tick will re-fire this
        // branch — the corrected workspace's module (navbar/pages/widgets/
        // seeds) stayed unloaded for the whole session (GreenIG cold-boot
        // "widgets empty" bug). _ensureWorkspaceModule is wsId-keyed +
        // seq-deduped, so this is a cheap no-op whenever the boot chain DID
        // load the right module.
        _ensureWorkspaceModule(activeWorkspace?.id || nextActiveWs)
        // WS-COLD-BOOT-IDENTITY-CORRECTION-KEEPS-PROVISIONAL-WORKSPACE-ROWS-1
        // — a BUILT-IN page mounted during the provisional window (History,
        // /admin/logs, /admin/usage) already read against the WRONG id and
        // will not read again on its own: each only listens for
        // `onActiveWorkspaceFull` while it has no id at all. Tell every such
        // loader the identity just corrected so it re-scopes to the real one.
        // NOT `onActiveWorkspaceFull` itself — a deliberate switch already
        // fires that one and re-hydrates its own mounted surfaces (PHASE 2);
        // reusing it here would read them twice on every deliberate switch.
        shellNotifyWorkspaceIdentityCorrected(root, activeWorkspace)
      }
    } else if (!nextId) {
      lastUserId = null
      lastUserEmail = null
      lastActiveWorkspaceId = null
      lastHealedActiveOrgId = null
      _lastRemintedOrgId = null
      // Re-arm the post-sign-in module load so a re-sign-in in this tab reloads
      // the workspace module (onWorkspaceChange owns the org-switch path).
      _moduleLoadedForWsId = null
    }
  }
  // The SDK proxy in shared/functions/sdk.js wraps method calls so they
  // auto-await SDK init — `getSDK().onAuthStateChange(cb)` therefore
  // returns Promise<unsub> instead of a sync unsub. Use a cancel-token
  // shim so callers (workspace-project app onCreate) can safely call
  // the returned unsub from onRemove without storing a Promise.
  let unsub = null
  let cancelled = false
  // PERF-1: dedup double-boot-fetch guard. Both `sdk.onAuthStateChange`
  // and the manual `sdk.getSession()` prime below resolve concurrently on
  // a warm boot (stored token). Without coordination, both calls reach the
  // `nextId !== lastUserId` check before either has set `lastUserId = nextId`
  // — so BOTH fire `onSignIn` → `loadPrivateData` runs twice, doubling
  // every boot fetch. Track whether the live auth-state subscription has
  // already delivered a non-empty userId so the manual prime becomes a
  // no-op on warm boots while still covering the cold-JWT path (expired
  // gov token, no INITIAL_SESSION event).
  let _authStateDelivered = false
  const _trackingHandler = (_event, session) => {
    if (session?.user?.id || session?.user?._id) _authStateDelivered = true
    return handler(_event, session)
  }
  const sdk = shellGetSDK()
  Promise.resolve(sdk.onAuthStateChange(_trackingHandler))
    .then((sub) => {
      if (cancelled) {
        if (typeof sub === 'function') {
          try {
            sub()
          } catch (_) {}
        }
        return
      }
      unsub = typeof sub === 'function' ? sub : null
    })
    .catch(() => {})
  // Initial-session priming.
  //
  // The SDK's onAuthStateChange keeps its legacy event contract — it does
  // NOT replay an `INITIAL_SESSION` event for callers who subscribe after
  // the SDK has already booted. When the session was already live before
  // this subscriber registered, no auth event ever fires, so the handler
  // never runs and `root.currentUserEmail` stays
  // empty. That left WeekView / TeamView grids rendering zero events
  // because they filter by `ev.googleCalendarId === root.currentUserEmail`.
  //
  // Synchronously prime the handler with whatever the SDK already knows
  // about the active session. Safe to call even when nothing's signed in —
  // the handler treats `session?.user == null` as a no-op via its
  // `userChanged` diff (lastUserId starts null, nextId stays '').
  //
  // PERF-1: skip the manual prime when `onAuthStateChange` already fired
  // with a live user — the auth-state subscription is authoritative on
  // warm boots and a second handler invocation races to fire `onSignIn`
  // a second time (both see lastUserId===null before either writes it).
  // We use a 0-delay microtask yield to let any synchronously-resolved
  // `onAuthStateChange` callbacks land first; the check is then accurate
  // for all synchronous SDK implementations. For truly async SDKs where
  // `onAuthStateChange` won't fire before the microtask, `_authStateDelivered`
  // is still false → we fall through and prime normally (the cold-JWT path).
  Promise.resolve()
    .then(() => sdk.getSession())
    .then((session) => {
      if (cancelled) return
      if (_authStateDelivered) return
      try {
        handler('INITIAL_SESSION', session)
      } catch (err) {
        console.warn('[auth] initial-session prime failed:', err?.message || err)
      }
    })
    .catch(() => {})
  return () => {
    cancelled = true
    if (typeof unsub === 'function') {
      try {
        unsub()
      } catch (_) {}
    }
  }
}

// Log in via the federation layer — governance always, plus any
// configured extensions (financials once its bridge is deployed).
// Return shape keeps the legacy signInWithPassword envelope so existing
// callers don't need to change.
export const signInWithPassword = async function (_email, _password) {
  // The SDK login upstream already established the session; return it in the
  // legacy-shaped envelope existing callers expect. (The old governance
  // federation `loginAll` hop was a no-op once federation was disabled and is
  // gone with the removed governance auth plane.)
  const session = await shellGetSDK().getSession()
  return {
    data: {
      user: session?.user || null,
      session: session || null,
      projects: []
    },
    error: null
  }
}
